import React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { useJobStore } from "~/stores/job-store";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";
import { JobTrackingLimitPrompt } from "~/components/JobTrackingLimitPrompt";
import { AtsBadge } from "~/components/AtsBadge";
import { detectAtsFromUrl, getAtsFormatRules } from "~/lib/ats-detection";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { PillTabs } from "~/components/shared/PillTabs";
import { ViewSwitcher } from "~/components/shared/ViewSwitcher";
import { TableIcon, CardIcon } from "~/components/shared/ViewIcons";
import { EmptyState } from "~/components/shared/EmptyState";
import { CompanyAvatar } from "~/components/CompanyAvatar";

/* status dot colors */
const STATUS_DOT_COLORS: Record<string, string> = {
  saved: "var(--text-muted)",
  applied: "var(--accent)",
  interview: "var(--warning)",
  rejected: "var(--error)",
  offer: "var(--success)",
};

export const Route = createFileRoute("/jobs/")({
  beforeLoad: async () => { await requireAuth(); },
  component: JobsListPage,
  pendingComponent: JobsListSkeleton,
  errorComponent: RouteErrorBoundary,
});

function JobsListSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-10 w-28 rounded-[10px]" />
      </div>
      <div className="mt-6">
        <Skeleton className="h-10 w-full rounded-lg" />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="mt-1 h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function JobsListPage() {
  const queryClient = useQueryClient();
  const jobStore = useJobStore();
  const { toast } = useToast();
  const [view, setView] = React.useState<"table" | "card">("table");

  const { data: orpcJobs, isError: orpcFailed } = useQuery({
    ...orpc.job.list.queryOptions(),
    retry: 0,
  });
  const jobs = orpcFailed ? jobStore.jobs : (orpcJobs ?? jobStore.jobs);

  const { data: newCount } = useQuery({
    ...orpc.jobSearch.newJobCount.queryOptions(),
    retry: 0,
  });

  const { data: tierData } = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    retry: 0,
  });
  const tier = tierData?.tier ?? "free";
  const jobTrackingLimit = tier === "free" ? 10 : -1;

  const deleteMutation = useMutation(orpc.job.delete.mutationOptions({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.job.list.key({}) }),
  }));

  function handleDelete(id: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(m.jobs_confirm_delete())) return;
    if (orpcFailed) {
      jobStore.deleteJob(id);
      toast(m.jobs_deleted(), "error");
    } else {
      deleteMutation.mutate({ id }, {
        onSuccess: () => toast(m.jobs_deleted(), "error"),
        onError: () => { jobStore.deleteJob(id); toast(m.jobs_deleted(), "error"); },
      });
    }
  }

  const statusConfig: Record<string, { label: string }> = {
    saved: { label: m.jobs_status_saved() },
    applied: { label: m.jobs_status_applied() },
    interview: { label: m.jobs_status_interview() },
    rejected: { label: m.jobs_status_rejected() },
    offer: { label: m.jobs_status_offer() },
  };

  return (
    <div className="animate-fade-up">
      <PillTabs tabs={[
        { label: m.jobs_tab_my_jobs(), to: "/jobs" },
        { label: m.jobs_tab_scraped(), to: "/jobs/scraped", badge: newCount?.count },
        { label: m.jobs_tab_search_criteria(), to: "/jobs/search-criteria" },
      ]} />

      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-[22px] font-semibold"
            style={{
              fontFamily: "var(--font-display)",
              letterSpacing: "-0.5px",
              color: "var(--text)",
            }}
          >
            {m.jobs_title()}
          </h1>
          <p className="mt-1 text-[13px]" style={{ color: "var(--text-muted)" }}>
            {m.jobs_tracked_count({ count: jobs.length, plural: jobs.length !== 1 ? "s" : "" })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ViewSwitcher
            views={[
              { id: "table", icon: TableIcon, label: "Table view" },
              { id: "card", icon: CardIcon, label: "Card view" },
            ]}
            active={view}
            onChange={(v) => setView(v as "table" | "card")}
          />
          <Link
            to="/jobs/new"
            className="inline-flex items-center rounded-lg px-3 py-1.5 text-[13px] font-medium text-white transition-colors"
            style={{ background: "var(--accent)" }}
          >
            + {m.jobs_add_job()}
          </Link>
        </div>
      </div>

      {/* Job tracking limit prompt for free tier */}
      <JobTrackingLimitPrompt
        currentCount={jobs.length}
        limit={jobTrackingLimit}
        tier={tier}
      />

      {jobs.length === 0 ? (
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
          }
          title={m.jobs_empty_title()}
          description={"Start by adding a job or setting up search criteria"}
          action={{ label: m.jobs_add_job(), href: "/jobs/new" }}
        />
      ) : view === "table" ? (
        /* Table View */
        <div
          className="border rounded-lg overflow-hidden"
          style={{ borderColor: "var(--border-subtle)", borderRadius: "8px" }}
        >
          {/* Header row */}
          <div
            className="flex px-4 py-2.5 text-[11px] font-semibold uppercase"
            role="row"
            style={{
              background: "var(--surface-raised)",
              borderBottom: "1px solid var(--border-subtle)",
              letterSpacing: "0.06em",
              color: "var(--text-muted)",
            }}
          >
            <span className="flex-[3]" role="columnheader">Company / Role</span>
            <span className="flex-1 hidden md:block" role="columnheader">Location</span>
            <span className="flex-1" role="columnheader">Status</span>
            <span className="flex-1 text-right" role="columnheader">Added</span>
          </div>
          {/* Data rows */}
          {jobs.map((j, i) => {
            const status = statusConfig[j.status] ?? statusConfig.saved;
            const dotColor = STATUS_DOT_COLORS[j.status] ?? STATUS_DOT_COLORS.saved;
            const dateStr = new Date(j.createdAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            });
            const ats = j.url ? detectAtsFromUrl(j.url) : null;

            return (
              <Link
                key={j.id}
                to="/jobs/$jobId"
                params={{ jobId: j.id }}
                className="flex items-center px-4 py-3 text-[13px] transition-colors group"
                role="row"
                style={{
                  borderBottom:
                    i < jobs.length - 1
                      ? "1px solid var(--border-subtle)"
                      : undefined,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "var(--surface-raised)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                }}
              >
                {/* Company / Role */}
                <span className="flex-[3] flex items-center gap-3 min-w-0" role="cell">
                  <CompanyAvatar
                    company={j.company || "?"}
                    url={j.url}
                    size={28}
                    radius="6px"
                  />
                  <span className="min-w-0">
                    <span
                      className="block font-medium leading-tight"
                      style={{ color: "var(--text)" }}
                    >
                      {j.title}
                    </span>
                    <span
                      className="flex items-center gap-1.5 text-[12px] leading-tight mt-0.5"
                      style={{ color: "var(--text-muted)" }}
                    >
                      <span>{j.company}</span>
                      {ats?.provider && (() => {
                        const rules = getAtsFormatRules(ats.provider);
                        const displayName = ats.provider.charAt(0).toUpperCase() + ats.provider.slice(1);
                        return <AtsBadge provider={displayName} tip={rules.tip} />;
                      })()}
                    </span>
                  </span>
                </span>
                {/* Location */}
                <span
                  className="flex-1 hidden md:block text-[12px]"
                  role="cell"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {(j as Record<string, unknown>).location as string ?? "\u2014"}
                </span>
                {/* Status */}
                <span className="flex-1 flex items-center gap-1.5" role="cell">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: dotColor }}
                    aria-hidden="true"
                  />
                  <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                    {status.label}
                  </span>
                </span>
                {/* Added */}
                <span
                  className="flex-1 text-right text-[12px] flex items-center justify-end gap-2"
                  role="cell"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span>{dateStr}</span>
                  <button
                    onClick={(e) => handleDelete(j.id, e)}
                    className="rounded-md p-1 text-[var(--text-muted)] opacity-0 transition-opacity duration-150 hover:bg-[var(--error-subtle)] hover:text-[var(--error)] group-hover:opacity-100"
                    aria-label={m.common_delete()}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </span>
              </Link>
            );
          })}
        </div>
      ) : (
        /* Card View */
        <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {jobs.map((j, index) => {
            const status = statusConfig[j.status] ?? statusConfig.saved;
            const dotColor = STATUS_DOT_COLORS[j.status] ?? STATUS_DOT_COLORS.saved;
            const stagger = `stagger-${Math.min(index + 1, 8)}`;
            const dateStr = new Date(j.createdAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            });

            return (
              <Link
                key={j.id}
                to="/jobs/$jobId"
                params={{ jobId: j.id }}
                className={`group flex flex-col justify-between rounded-lg p-4 transition-all duration-200 animate-fade-up ${stagger}`}
                style={{
                  border: "1px solid var(--border-subtle)",
                  background: "var(--surface)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "var(--surface-raised)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "var(--surface)";
                }}
              >
                {/* Top row: date + delete */}
                <div className="flex items-start justify-between">
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                    {dateStr}
                  </span>
                  <button
                    onClick={(e) => handleDelete(j.id, e)}
                    className="rounded-md p-1 text-[var(--text-muted)] opacity-100 sm:opacity-0 transition-opacity duration-150 hover:bg-[var(--error-subtle)] hover:text-[var(--error)] sm:group-hover:opacity-100"
                    aria-label={m.common_delete()}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>

                {/* Company logo + Title */}
                <div className="mt-3 flex items-start gap-3">
                  <CompanyAvatar
                    company={j.company || "?"}
                    url={j.url}
                    size={28}
                    radius="6px"
                  />
                  <div className="min-w-0">
                    <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>{j.company}</p>
                    <h3
                      className="mt-0.5 text-[15px] font-semibold leading-tight line-clamp-2"
                      style={{ color: "var(--text)" }}
                    >
                      {j.title}
                    </h3>
                  </div>
                </div>

                {/* Status */}
                <div className="mt-3 flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: dotColor }}
                    aria-hidden="true"
                  />
                  <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                    {status.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
